import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import SuburbInput from "../components/SuburbInput";

const DISCIPLINES = ["Residential Architecture", "Residential Interiors", "Commercial Architecture", "Commercial Fit-outs"];

/** CSS-only hero preview using the same object-position/scale used on the public site */
function heroStyle(p) {
  const fx = p?.hero_focal_x ?? 50, fy = p?.hero_focal_y ?? 50;
  const scale = (p?.hero_scale ?? 100) / 100;
  return { objectFit: "cover", objectPosition: `${fx}% ${fy}%`, transform: `scale(${scale})`, transformOrigin: `${fx}% ${fy}%` };
}

export default function AdminProjectEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const imgFileRef = useRef(null);
  const heroFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  // Upload-progress state — percentage 0-100 for the currently active upload.
  // `imageProgress` is a map keyed by a per-file id so multiple gallery uploads
  // can show simultaneous bars if we later fire them in parallel.
  const [heroProgress, setHeroProgress] = useState(0);
  const [imageProgress, setImageProgress] = useState({}); // { [key]: {name, pct} }
  const [aiBusy, setAiBusy] = useState(null); // image id currently generating
  const [local, setLocal] = useState(null);
  const [dragId, setDragId] = useState(null);

  const { data: p } = useQuery({
    queryKey: ["admin", "project", id],
    queryFn: async () => (await api.get(`/admin/projects/${id}`)).data,
  });

  useEffect(() => { if (p) setLocal(p); }, [p]);

  const saveMut = useMutation({
    mutationFn: async (changes) => (await api.patch(`/admin/projects/${id}`, changes)).data,
    onSuccess: (data) => {
      setLocal(data);
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
      qc.invalidateQueries({ queryKey: ["admin", "project", id] });
    },
    onError: () => toast.error("Save failed."),
  });

  // Debounced crop save (focal + scale only)
  const cropDebounce = useRef(null);
  const commitCrop = (patch) => {
    setLocal((l) => ({ ...l, ...patch }));
    if (cropDebounce.current) clearTimeout(cropDebounce.current);
    cropDebounce.current = setTimeout(() => saveMut.mutate(patch), 400);
  };

  const uploadImage = async (file) => {
    if (!preflight(file, "image")) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt", local?.title || "Project image");
    const key = `${file.name}-${file.size}-${Date.now()}`;
    try {
      setUploading(true);
      setImageProgress((m) => ({ ...m, [key]: { name: file.name, pct: 0 } }));
      await api.post(`/admin/projects/${id}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          setImageProgress((m) => ({ ...m, [key]: { name: file.name, pct } }));
        },
      });
      qc.invalidateQueries({ queryKey: ["admin", "project", id] });
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error(formatUploadError(e, file), { duration: 8000 });
    } finally {
      setUploading(false);
      // Keep the row visible for a beat so the user sees "100%" before it disappears.
      setTimeout(() => setImageProgress((m) => { const n = { ...m }; delete n[key]; return n; }), 800);
    }
  };

  // Robust video detection — .mov / .m4v from macOS/iOS often arrive with
  // file.type = "" or "application/octet-stream", so fall back to the file
  // extension. Matches the extensions accepted by the backend.
  const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv|ogg|mkv|avi)$/i;
  const isVideoFile = (file) =>
    (file.type && file.type.startsWith("video/")) || VIDEO_EXT.test(file.name);

  // Cloudflare's free/pro tier rejects request bodies larger than ~100MB and
  // times out uploads that take longer than ~100 seconds. We cap the client
  // at 90MB with a clear message so admins never see the mystery CF error.
  const MAX_UPLOAD_MB = { image: 25, video: 90 };
  const humanMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1);
  const preflight = (file, kind) => {
    const limit = MAX_UPLOAD_MB[kind];
    if (file.size > limit * 1024 * 1024) {
      toast.error(
        `${file.name} is ${humanMB(file.size)}MB — too large. ${kind === "video"
          ? "Compress the video to under 90MB (try HandBrake or QuickTime → Export as 1080p) and try again."
          : "Please resize to under 25MB."}`,
        { duration: 8000 },
      );
      return false;
    }
    return true;
  };

  // Drop-zone state — one per zone so both can highlight independently.
  const [heroDrag, setHeroDrag] = useState(false);
  const [galleryDrag, setGalleryDrag] = useState(false);

  const dropHero = (e) => {
    e.preventDefault();
    setHeroDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadHero(file);
  };
  const dropGallery = async (e) => {
    e.preventDefault();
    setGalleryDrag(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    // No files at all = internal reorder drag bubbled up; silently ignore
    // instead of showing a "drop image files…" error toast.
    if (dropped.length === 0) return;
    const files = dropped.filter((f) =>
      f.type.startsWith("image/") || /\.(jpg|jpeg|png|webp|avif|gif|heic|heif)$/i.test(f.name),
    );
    if (files.length === 0) {
      toast.error("Drop image files (jpg/png/webp) into the gallery zone.");
      return;
    }
    for (const f of files) { await uploadImage(f); }
  };

  // Turn an axios/network error into a human-friendly message. Cloudflare
  // rejects oversize / slow requests with an HTML error page, so response
  // data is a string, not JSON — surface a clear hint in that case.
  const formatUploadError = (e, file) => {
    const detail = e?.response?.data?.detail;
    if (detail) return detail;
    const status = e?.response?.status;
    const data = e?.response?.data;
    const looksLikeHtml = typeof data === "string" && data.includes("<html");
    if (looksLikeHtml || status === 413 || status === 524 || status === 520 || status === 522) {
      return `Cloudflare blocked the upload of ${file.name} (${humanMB(file.size)}MB) — likely too large or the connection timed out. Try a smaller / compressed file.`;
    }
    if (!e?.response) {
      return `Network dropped while uploading ${file.name}. Check your connection and try a smaller file.`;
    }
    return `Upload failed (${status || "network"}) — ${file.name}.`;
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const isVideo = isVideoFile(file);
    if (!preflight(file, isVideo ? "video" : "image")) return;
    try {
      setHeroUploading(true);
      setHeroProgress(0);
      const onUploadProgress = (evt) => {
        if (!evt.total) return;
        setHeroProgress(Math.round((evt.loaded * 100) / evt.total));
      };
      if (isVideo) {
        const fd = new FormData();
        fd.append("file", file);
        // Force a sensible content-type when the browser gave us none — matters
        // for Safari-uploaded .mov files that come through as octet-stream.
        const forcedType = file.type && file.type.startsWith("video/")
          ? file.type
          : (/\.mov$/i.test(file.name) ? "video/quicktime"
            : /\.webm$/i.test(file.name) ? "video/webm"
            : /\.mkv$/i.test(file.name) ? "video/x-matroska"
            : "video/mp4");
        if (!file.type || !file.type.startsWith("video/")) {
          // Re-wrap the Blob so the outgoing multipart part gets the right type
          fd.set("file", new File([file], file.name, { type: forcedType }));
        }
        await api.post(`/admin/projects/${id}/hero-video`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
        });
      } else {
        // Upload as an image, then set as hero
        const fd = new FormData();
        fd.append("file", file);
        fd.append("alt", local?.title || "Hero image");
        const { data: img } = await api.post(`/admin/projects/${id}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
        });
        await api.patch(`/admin/projects/${id}`, { hero_image_id: img.id, hero_media_type: "image" });
      }
      qc.invalidateQueries({ queryKey: ["admin", "project", id] });
      toast.success(isVideo ? "Hero video set." : "Hero image set.");
    } catch (e) {
      toast.error(formatUploadError(e, file), { duration: 8000 });
    } finally {
      setTimeout(() => { setHeroUploading(false); setHeroProgress(0); }, 600);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Remove this image?")) return;
    await api.delete(`/admin/projects/${id}/images/${imageId}`);
    qc.invalidateQueries({ queryKey: ["admin", "project", id] });
  };

  const setHero = (imageId) => saveMut.mutate({ hero_image_id: imageId, hero_media_type: "image" });

  const onDragStart = (imgId) => setDragId(imgId);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, targetId) => {
    // Stop the drop from bubbling to the section-level `dropGallery` handler,
    // which would fire a "Drop image files…" error because internal drags
    // don't carry files. (Code review 2026-02-25 MEDIUM #1)
    e.stopPropagation();
    if (!dragId || !local) return;
    const order = [...local.gallery.map((g) => g.id)];
    const from = order.indexOf(dragId);
    const to = order.indexOf(targetId);
    if (from === -1 || to === -1) return;
    order.splice(to, 0, order.splice(from, 1)[0]);
    setDragId(null);
    saveMut.mutate({ gallery_order: order });
  };

  if (!local) return <div className="pt-40 container-w mono-label">Loading…</div>;

  const heroImage = local.gallery?.find((g) => g.id === local.hero_image_id) || local.gallery?.[0];
  const isVideoHero = local.hero_media_type === "video" && local.hero_video_path;

  return (
    <div className="pt-28 md:pt-36 container-w pb-40" data-testid="admin-edit-page">
      <div className="mono-label mb-4"><Link to="/admin" className="link-underline">← Projects</Link></div>
      <div className="flex justify-between items-start mb-12 flex-wrap gap-4">
        <div>
          <div className="mono-label mb-2 text-ash">Editing project</div>
          <h1 className="font-display italic text-4xl md:text-6xl tracking-tight">{local.title || "Untitled"}</h1>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => saveMut.mutate({ published: !local.published })}
            className="mono-label px-6 py-3 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500"
            data-testid="toggle-publish"
          >
            {local.published ? "● Published" : "○ Publish"} →
          </button>
          <button
            onClick={() => saveMut.mutate({ featured: !local.featured })}
            className="mono-label px-6 py-3 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500"
            title={local.featured ? "Currently shown on Home — click to remove" : "Click to add this project to the Home page"}
            data-testid="toggle-featured"
          >
            {local.featured ? "★ On Home" : "☆ Add to Home"}
          </button>
          {local.published && (
            <Link to={`/work/${local.slug}`} target="_blank" className="mono-label px-6 py-3 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500">Preview →</Link>
          )}
        </div>
      </div>

      {/* ---------- 5-category form ---------- */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
        <div className="md:col-span-3 mono-label">a) Project name</div>
        <label className="md:col-span-9 block">
          <input
            type="text"
            value={local.title || ""}
            onChange={(e) => setLocal({ ...local, title: e.target.value })}
            onBlur={() => saveMut.mutate({ title: local.title })}
            className="w-full bg-transparent border-b border-ink/20 py-2 text-3xl md:text-5xl font-display italic focus:outline-none focus:border-marine"
            data-testid="field-title"
          />
        </label>

        <div className="md:col-span-3 mono-label">b) Short description</div>
        <label className="md:col-span-9 block">
          <textarea
            rows={2}
            value={local.summary || ""}
            onChange={(e) => setLocal({ ...local, summary: e.target.value })}
            onBlur={() => saveMut.mutate({ summary: local.summary })}
            className="w-full bg-transparent border-b border-ink/20 py-2 text-lg focus:outline-none focus:border-marine resize-none"
            placeholder="Used on cards, index and project header."
            data-testid="field-summary"
          />
        </label>

        <div className="md:col-span-3 mono-label">c) Big description</div>
        <label className="md:col-span-9 block">
          <textarea
            rows={7}
            value={local.body || ""}
            onChange={(e) => setLocal({ ...local, body: e.target.value })}
            onBlur={() => saveMut.mutate({ body: local.body })}
            className="w-full bg-transparent border-b border-ink/20 py-2 font-display italic text-xl leading-relaxed focus:outline-none focus:border-marine resize-none"
            placeholder="Full write-up shown on the detailed project page."
            data-testid="field-body"
          />
        </label>

        <div className="md:col-span-3 mono-label">Meta</div>
        <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-3 gap-6">
          <label className="block">
            <span className="mono-label block mb-2">Discipline</span>
            <select value={local.discipline} onChange={(e) => { setLocal({ ...local, discipline: e.target.value }); saveMut.mutate({ discipline: e.target.value }); }}
              className="w-full bg-transparent border-b border-ink/20 py-2 mono-label focus:outline-none" data-testid="field-discipline">
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
              {[["year", "Year"], ["status", "Status"], ["scope", "Scope"], ["slug", "Slug"]].map(([k, label]) => (
            <label key={k} className="block">
              <span className="mono-label block mb-2">{label}</span>
              <input
                type="text" value={local[k] || ""}
                onChange={(e) => setLocal({ ...local, [k]: e.target.value })}
                onBlur={() => saveMut.mutate({ [k]: local[k] })}
                className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-marine"
                data-testid={`field-${k}`}
              />
            </label>
          ))}
          <label className="block md:col-span-2">
            <span className="mono-label block mb-2">Location — starts searching Australian suburbs after 2 chars</span>
            <SuburbInput
              value={local.location || ""}
              onChange={(v) => setLocal((l) => ({ ...l, location: v }))}
              onCommit={(v) => { setLocal((l) => ({ ...l, location: v })); saveMut.mutate({ location: v }); }}
              className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-marine"
              placeholder="e.g. Coogee NSW"
              data-testid="field-location"
            />
          </label>
        </div>
        <label className="md:col-span-2 block">
          <span className="mono-label block mb-2">Featured in <span className="text-ash">— press / awards / publications (comma-separated, optional)</span></span>
          <input
            type="text"
            value={(local.featured_in || []).join(", ")}
            onChange={(e) => setLocal({ ...local, featured_in: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            onBlur={() => saveMut.mutate({ featured_in: local.featured_in || [] })}
            className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-marine"
            placeholder="e.g. Vogue Living, Est Living, Habitus"
            data-testid="field-featured-in"
          />
        </label>
        <label className="md:col-span-2 block">
          <span className="mono-label block mb-2">Meta description <span className="text-ash">— SEO (auto-filled from summary if left blank, max 160 chars)</span></span>
          <textarea
            rows={2}
            value={local.meta_description || ""}
            onChange={(e) => setLocal({ ...local, meta_description: e.target.value.slice(0, 160) })}
            onBlur={() => saveMut.mutate({ meta_description: local.meta_description })}
            maxLength={160}
            className="w-full bg-transparent border-b border-ink/20 py-2 text-sm focus:outline-none focus:border-marine resize-none"
            placeholder="Auto-derived from summary if left blank."
            data-testid="field-meta"
          />
        </label>
      </section>
      <section className="border-t border-ink/15 pt-12 mb-16">
        <div
          className={`grid grid-cols-1 md:grid-cols-12 gap-8 relative transition-colors duration-200 ${heroDrag ? "outline outline-2 outline-dashed outline-marine bg-marine/5" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setHeroDrag(true); }}
          onDragEnter={(e) => { e.preventDefault(); setHeroDrag(true); }}
          onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setHeroDrag(false); }}
          onDrop={dropHero}
          data-testid="hero-dropzone"
        >
          {heroDrag && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="bg-paper/95 border border-marine px-6 py-4 mono-label text-marine">
                Drop to set hero — image or video
              </div>
            </div>
          )}
          <div className="md:col-span-3">
            <div className="mono-label mb-2">d) Hero picture</div>
            <p className="text-ash text-sm mb-2">Image or video. Drag &amp; drop here, or click to browse. Adjust position &amp; zoom to fit the frame — changes save automatically.</p>
            <p className="mono-label text-ash/70 mb-4" style={{ fontSize: "10px" }}>
              Max: image 25MB · video 90MB. Larger videos are blocked by the CDN — compress first (HandBrake or QuickTime → Export 1080p).
            </p>
            <button
              className="mono-label px-6 py-3 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500 mb-3 block w-full"
              onClick={() => heroFileRef.current?.click()}
              disabled={heroUploading}
              data-testid="upload-hero-btn"
            >
              {heroUploading ? `Uploading… ${heroProgress}%` : `Upload ${isVideoHero ? "new video" : "image or video"}`}
            </button>
            {/* Hero upload progress bar */}
            {heroUploading && (
              <div className="mb-3" data-testid="hero-upload-progress">
                <div className="h-1.5 bg-ink/10 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-marine transition-[width] duration-200 ease-out"
                    style={{ width: `${heroProgress}%` }}
                  />
                </div>
                <div className="mono-label text-ash mt-1 flex items-center justify-between">
                  <span>{heroProgress < 100 ? "Uploading…" : "Finalising…"}</span>
                  <span className="tabular-nums text-marine">{heroProgress}%</span>
                </div>
              </div>
            )}
            <input
              ref={heroFileRef}
              type="file"
              accept="image/*,video/*,.mp4,.webm,.mov,.m4v,.mkv"
              className="hidden"
              data-testid="hero-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadHero(f);
                e.target.value = "";
              }}
            />
            {isVideoHero && (
              <button onClick={() => saveMut.mutate({ hero_media_type: "image" })} className="mono-label text-ash hover:text-marine block mb-3" data-testid="hero-clear-video">
                ← Use image hero instead
              </button>
            )}

            {/* Focal + scale controls */}
            <div className="space-y-4 mt-6">
              <label className="block">
                <span className="mono-label block mb-1">Horizontal focus · {Math.round(local.hero_focal_x ?? 50)}%</span>
                <input
                  type="range" min="0" max="100" step="1"
                  value={local.hero_focal_x ?? 50}
                  onChange={(e) => commitCrop({ hero_focal_x: parseFloat(e.target.value) })}
                  className="w-full accent-marine"
                  data-testid="hero-focal-x"
                />
              </label>
              <label className="block">
                <span className="mono-label block mb-1">Vertical focus · {Math.round(local.hero_focal_y ?? 50)}%</span>
                <input
                  type="range" min="0" max="100" step="1"
                  value={local.hero_focal_y ?? 50}
                  onChange={(e) => commitCrop({ hero_focal_y: parseFloat(e.target.value) })}
                  className="w-full accent-marine"
                  data-testid="hero-focal-y"
                />
              </label>
              <label className="block">
                <span className="mono-label block mb-1">Zoom · {Math.round(local.hero_scale ?? 100)}%</span>
                <input
                  type="range" min="100" max="250" step="1"
                  value={local.hero_scale ?? 100}
                  onChange={(e) => commitCrop({ hero_scale: parseFloat(e.target.value) })}
                  className="w-full accent-marine"
                  data-testid="hero-scale"
                />
              </label>
              <button
                onClick={() => commitCrop({ hero_focal_x: 50, hero_focal_y: 50, hero_scale: 100 })}
                className="mono-label link-underline text-ash"
                data-testid="hero-reset-crop"
              >Reset crop</button>
            </div>
          </div>

          {/* Preview */}
          <div className="md:col-span-9">
            <div className="w-full aspect-[16/9] md:aspect-[16/9] overflow-hidden bg-ink" data-testid="hero-preview">
              {isVideoHero ? (
                <video src={fileUrl(local.hero_video_path)} autoPlay muted loop playsInline
                  className="w-full h-full" style={heroStyle(local)} />
              ) : heroImage ? (
                <img src={fileUrl(heroImage.variants.full)} alt={heroImage.alt}
                  className="w-full h-full" style={heroStyle(local)} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-paper/60 mono-label">Upload a hero image or video</div>
              )}
            </div>
            <div className="mono-label mt-3 text-ash">Preview matches the public hero frame.</div>
          </div>
        </div>
      </section>

      {/* ---------- e) Gallery ---------- */}
      <section
        className={`border-t border-ink/15 pt-12 relative transition-colors duration-200 ${galleryDrag ? "outline outline-2 outline-dashed outline-marine bg-marine/5" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setGalleryDrag(true); }}
        onDragEnter={(e) => { e.preventDefault(); setGalleryDrag(true); }}
        onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setGalleryDrag(false); }}
        onDrop={dropGallery}
        data-testid="gallery-dropzone"
      >
        {galleryDrag && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="bg-paper/95 border border-marine px-6 py-4 mono-label text-marine">
              Drop images to add to the gallery
            </div>
          </div>
        )}
        <div className="flex justify-between items-baseline mb-8 flex-wrap gap-4">
          <div>
            <div className="mono-label mb-2">e) Gallery</div>
            <h2 className="font-display italic text-3xl md:text-4xl tracking-tight">Multiple pictures</h2>
            <p className="text-ash text-sm mt-1">Drag &amp; drop images here, or click below. Drag existing plates to reorder. Click ★ to set as hero.</p>
          </div>
          <button
            className="mono-label px-6 py-3 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500"
            onClick={() => imgFileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-image-btn"
          >
            {uploading ? "Uploading…" : "+ Add image(s)"}
          </button>
          <input
            ref={imgFileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="image-file-input"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              for (const f of files) { await uploadImage(f); }
              e.target.value = "";
            }}
          />
        </div>
        {/* Gallery upload progress — one row per in-flight file */}
        {Object.keys(imageProgress).length > 0 && (
          <div className="mb-6 space-y-2" data-testid="gallery-upload-progress">
            {Object.entries(imageProgress).map(([k, v]) => (
              <div key={k}>
                <div className="mono-label text-ash flex items-center justify-between mb-1">
                  <span className="truncate pr-3">{v.name}</span>
                  <span className="tabular-nums text-marine">{v.pct}%</span>
                </div>
                <div className="h-1.5 bg-ink/10 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-marine transition-[width] duration-200 ease-out"
                    style={{ width: `${v.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        {local.gallery?.length === 0 && (
          <div className="py-16 text-center mono-label text-ash border-y border-ink/15">No images yet. Add the first plate.</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-gallery">
          {local.gallery?.map((img) => (
            <div
              key={img.id}
              className={`relative group ${dragId === img.id ? "opacity-40" : ""}`}
              draggable
              onDragStart={() => onDragStart(img.id)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, img.id)}
              data-testid={`gallery-item-${img.id}`}
            >
              <img src={fileUrl(img.variants.grid)} alt={img.alt} className="w-full aspect-[4/3] object-cover" />
              <div className="absolute inset-x-0 top-0 p-2 flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button
                  disabled={aiBusy === img.id}
                  onClick={async () => {
                    if (aiBusy) return;
                    setAiBusy(img.id);
                    try {
                      const resp = await fetch(fileUrl(img.variants.full));
                      const blob = await resp.blob();
                      const fd = new FormData();
                      fd.append("file", blob, "img.webp");
                      toast.loading("Writing a description…", { id: "ai" });
                      const { data } = await api.post(`/admin/ai/describe-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      const desc = data.description;
                      toast.dismiss("ai");
                      if (!desc) { toast.error("No description returned."); return; }
                      const newBody = (local.body ? local.body + "\n\n" : "") + desc;
                      setLocal({ ...local, body: newBody });
                      saveMut.mutate({ body: newBody });
                      toast.success("Added to Big description.");
                    } catch (e) {
                      toast.dismiss("ai");
                      toast.error("AI description unavailable.");
                    } finally {
                      setAiBusy(null);
                    }
                  }}
                  className="mono-label px-2 py-1 bg-paper/95 border border-ink/20 hover:bg-marine hover:text-paper hover:border-marine transition-colors duration-300 disabled:opacity-40 disabled:cursor-wait"
                  data-testid={`ai-describe-${img.id}`}
                  style={{ fontSize: "9px", letterSpacing: "0.1em" }}
                >
                  {aiBusy === img.id ? "✧ Writing…" : "✧ AI describe"}
                </button>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between mono-label bg-paper/90">
                <button onClick={() => setHero(img.id)}
                  className={local.hero_image_id === img.id && !isVideoHero ? "text-marine" : "hover:text-marine"}
                  data-testid={`set-hero-${img.id}`}>
                  {local.hero_image_id === img.id && !isVideoHero ? "★ Hero" : "Set hero"}
                </button>
                <button onClick={() => deleteImage(img.id)} className="hover:text-marine" data-testid={`delete-image-${img.id}`}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
